(function () {
  'use strict';
  if (window.LPDirectCheckout) return;
  let pending = null;
  let frame = null;
  let cartDrawerView = null;
  const ADD = '.mw-group__button-next--addtocart';
  const CANDIDATES = ADD + ', .mw-group__button-next';
  const GIFT_CANDIDATES = '.mw-apo-add-to-cart-custom, .mw-apo-add-to-cart-cloned, .product-form__submit[name="add"]';
  const GIFT_EXCLUSIONS = 'quick-add-modal, .mw-tingle, product-recommendations, .product-card-wrapper, .complementary-products';
  const receipts = new WeakMap();
  const returnedCarts = new WeakMap();
  let returnedCartRequest = 0;
  const wrappedDrawers = new WeakSet();

  const draft = { data: null, saved: null, restoring: false, dirty: false, timer: null,
    generation: 0, records: new Map(), notice: null };

  function portraitContext(scope) {
    const tagged = (window.mwApoInit?.product?.tags || []).some(tag => /(^|[-_\s])(2d|3d)(?=$|[-_\s])/i.test(tag));
    return (document.body.classList.contains('lp-portrait-pdp') || tagged) && scope &&
      !scope.closest('quick-add-modal, .mw-tingle') && !!(scope.closest('[id^="MainProduct-"]') || scope.querySelector('[id^="MainProduct-"]'));
  }

  function draftScope(data = draft.data) {
    return data?.htmlElements?.options?.closest('[id^="MainProduct-"]') || null;
  }

  function draftStorageKey(data = draft.data) {
    return 'lp-portrait-draft:v1:' + data.loadedState.product_id;
  }

  function draftVariant(data = draft.data) {
    const variants = data?.initConfig?.product?.variants || [];
    const requested = new URL(location.href).searchParams.get('variant');
    const selected = variants.find(variant => String(variant.id) === requested);
    const native = data?.htmlElements?.form?.querySelector('[name="id"]')?.value;
    const fallback = variants.find(variant => String(variant.id) === String(native));
    return Number(selected?.id || fallback?.id || data?.loadedState?.variant_id || 0);
  }

  function draftConfiguration() {
    const data = draft.data;
    if (!portraitContext(draftScope(data))) return null;
    const html = data.loadedState.html;
    const sdk = window.mwApo?.getInstance(data.sdkBlockId);
    const selected = sdk?.getSelections?.() || [...html.querySelectorAll('input:checked')].map(field => field.value);
    const inputs = [];
    for (const [id, value] of Object.entries(data.loadedState.values)) {
      const field = html.querySelector('#' + CSS.escape(id));
      const file = /file/i.test(value.value?.input_type || '') ||
        /file/i.test(data.loadedState.options[value.option_id]?.input_type || '');
      if (file || (field && /^(INPUT|TEXTAREA)$/.test(field.tagName) &&
          !/^(radio|checkbox|hidden|submit|button)$/.test(field.type))) {
        inputs.push({ id, file, value: file ? String(value.user_input || '') : field.value });
      }
    }
    inputs.sort((a, b) => a.id.localeCompare(b.id));
    return { variantId: draftVariant(data), selected: [...selected].sort(), inputs };
  }

  function draftConfigurationKey(configuration) {
    if (!configuration) return '';
    return JSON.stringify({ ...configuration, inputs: configuration.inputs.map(input => ({ ...input,
      value: input.file ? (input.value.match(/https?:\/\/[^\s"',\]}]+/g) || []) : input.value })) });
  }

  function draftStep() {
    const group = draft.data?.loadedState?.html?.querySelector('.mw-group:not(.mw-group__hidden)');
    return Number(group?.dataset.groupIndex || 0);
  }

  function readProductDraft(data) {
    try {
      const saved = JSON.parse(sessionStorage.getItem(draftStorageKey(data)) || 'null');
      if (saved?.version !== 1 || saved.productId !== data.loadedState.product_id ||
          !Array.isArray(saved.configuration?.selected) || !Array.isArray(saved.configuration?.inputs)) return null;
      const requested = new URL(location.href).searchParams.get('variant');
      if (requested && String(saved.variantId) !== requested) return null;
      return saved;
    } catch (_) { return null; }
  }

  function saveProductDraft() {
    clearTimeout(draft.timer);
    if (draft.restoring || !draft.dirty || !portraitContext(draftScope())) return;
    const configuration = draftConfiguration();
    if (!configuration) return;
    const saved = { version: 1, productId: draft.data.loadedState.product_id,
      variantId: configuration.variantId, configuration, step: draftStep(), scrollY: window.scrollY,
      records: [...draft.records].slice(-8).map(([key, record]) => ({ key, expected: record.receipt.expected })) };
    try {
      const value = JSON.stringify(saved);
      if (value.length < 65536) sessionStorage.setItem(draftStorageKey(), value);
    } catch (_) { /* Storage availability must never prevent adding or checking out. */ }
  }

  function queueProductDraft() {
    if (!portraitContext(draftScope()) || draft.restoring) return;
    draft.dirty = true;
    clearTimeout(draft.timer);
    draft.timer = setTimeout(saveProductDraft, 350);
  }

  function syncDraftReceipt(block, context) {
    if (pending || draft.restoring || context.gift || !portraitContext(context.scope) || !draft.data) return;
    if (!draft.records.size) { returnedCarts.delete(block); return; }
    const record = draft.records.get(draftConfigurationKey(draftConfiguration()));
    if (!record) { returnedCarts.delete(block); return; }
    if (record.present === false) receipts.delete(block);
    else {
      record.receipt.signature = signature(context.scope);
      receipts.set(block, record.receipt);
    }
    returnedCarts.set(block, record);
  }

  function markDraftAdded(intent) {
    if (intent.gift || !intent.receipt || intent.receipt.partial || !intent.draftKey || !portraitContext(intent.scope)) return;
    draft.records.set(intent.draftKey, { receipt: intent.receipt, present: true });
    draft.dirty = true;
    saveProductDraft();
    schedule();
  }

  function updateDraftCart(cart) {
    draft.records.forEach(record => { record.present = returnedItemPresent(cart, record.receipt); });
  }

  function finishDraftRestore(data, generation, message = '') {
    if (draft.generation !== generation || draft.data !== data) return;
    data.htmlElements.options.inert = false;
    data.htmlElements.options.removeAttribute('aria-busy');
    if (draft.notice) {
      if (message) draft.notice.textContent = message;
      else draft.notice.remove();
    }
    draft.restoring = false;
    if (!message) { draft.dirty = true; saveProductDraft(); }
    schedule();
  }

  function waitForDraftFiles(data, configuration) {
    const expected = configuration.inputs.filter(input => input.file).reduce((count, input) => count + (input.value.match(/https?:\/\//g) || []).length, 0);
    if (!expected) return Promise.resolve();
    return new Promise((resolve, reject) => {
      const html = data.loadedState.html;
      const done = () => {
        if (html.querySelectorAll('.filepond--file-info-main').length < expected) return;
        clearTimeout(timeout); observer.disconnect(); resolve();
      };
      const observer = new MutationObserver(done);
      const timeout = setTimeout(() => { observer.disconnect(); reject(new Error('Photo restore timed out')); }, 10000);
      observer.observe(html, { childList: true, subtree: true });
      done();
    });
  }

  async function restoreProductDraft(data, saved, generation) {
    const scope = draftScope(data);
    const cartPromise = draft.records.size ? readCart().catch(() => null) : Promise.resolve(null);
    const timeout = setTimeout(() => {
      finishDraftRestore(data, generation, 'Please review your customization before continuing.');
      if (draft.generation === generation) ++draft.generation;
    }, 12000);
    try {
      if (!window.mwApo?.ready) throw new Error('Options restore is unavailable');
      const sdk = await window.mwApo.ready(data.sdkBlockId);
      if (draft.generation !== generation || !draft.restoring) return;
      sdk.setValues(saved.configuration.selected);
      for (const input of saved.configuration.inputs) {
        if (input.file) continue;
        const field = data.loadedState.html.querySelector('#' + CSS.escape(input.id));
        if (!field || !/^(INPUT|TEXTAREA)$/.test(field.tagName)) continue;
        field.value = input.value;
        field.dispatchEvent(new Event('input', { bubbles: true }));
        field.dispatchEvent(new Event('change', { bubbles: true }));
      }
      await waitForDraftFiles(data, saved.configuration);
      const cart = await cartPromise;
      if (draft.generation !== generation || !draft.restoring) return;
      if (cart) updateDraftCart(cart);
      const target = Math.min(Number(saved.step) || 0, data.loadedState.html.querySelectorAll('.mw-group').length - 1);
      for (let index = 0; index < target; index += 1) {
        const before = draftStep();
        const next = [...scope.querySelectorAll('.mw-group__button-next')].find(button =>
          button.getClientRects().length && !button.classList.contains('mw-group__button-next--addtocart'));
        if (!next || before >= target) break;
        next.click();
        await new Promise(resolve => requestAnimationFrame(resolve));
        if (draftStep() === before) break;
      }
      finishDraftRestore(data, generation);
      requestAnimationFrame(() => {
        if (draft.generation === generation) window.scrollTo(0, Math.max(0, Number(saved.scrollY) || 0));
      });
    } catch (_) {
      finishDraftRestore(data, generation, 'Please review your customization before continuing.');
    } finally { clearTimeout(timeout); }
  }

  function handleDraftApoEvent(name, data) {
    if (name === 'init::insertIntoInline::afterHtmlInserted') {
      if (!portraitContext(draftScope(data))) return;
      const first = !draft.data;
      const replacing = draft.restoring && draft.saved;
      draft.data = data;
      if (!first && !replacing) return;
      const early = window.__LP_PORTRAIT_DRAFT_INIT__;
      const saved = replacing || (early?.data === data ? early.saved : readProductDraft(data));
      if (!saved) return;
      draft.saved = saved;
      draft.restoring = true;
      ++draft.generation;
      for (const record of Array.isArray(saved.records) ? saved.records : []) {
        if (typeof record.key !== 'string' || !Array.isArray(record.expected) || !record.expected.length) continue;
        if (!record.expected.every(item => /^\d+$/.test(item.id) && item.properties && typeof item.properties === 'object')) continue;
        draft.records.set(record.key, { receipt: { expected: record.expected, partial: false }, present: null });
      }
      for (const input of saved.configuration.inputs) {
        const value = data.loadedState.values[input.id];
        if (!value || typeof input.value !== 'string') continue;
        value.user_input = input.value;
        value.enabled = !!input.value.trim();
        if (!input.file) {
          const field = data.loadedState.html.querySelector('#' + CSS.escape(input.id));
          if (field && /^(INPUT|TEXTAREA)$/.test(field.tagName)) field.value = input.value;
        }
      }
      data.htmlElements.options.inert = true;
      data.htmlElements.options.setAttribute('aria-busy', 'true');
      draft.notice?.remove();
      draft.notice = document.createElement('p');
      draft.notice.setAttribute('role', 'status');
      draft.notice.textContent = 'Restoring your customization…';
      data.htmlElements.options.before(draft.notice);
    }
    if (name === 'init::finish' && data === draft.data && draft.restoring && draft.saved) {
      restoreProductDraft(data, draft.saved, draft.generation);
    } else if (draft.dirty && !draft.restoring) {
      queueProductDraft();
    }
  }


  function propertyValue(value) {
    // APO's cart writer uses join(', '), not JavaScript's default array coercion.
    return Array.isArray(value) ? value.join(', ') : String(value ?? '');
  }

  function propertiesFor(properties) {
    const result = {};
    Object.entries(properties || {}).forEach(([key, value]) => {
      const text = propertyValue(value);
      const publicKey = key.replace(/^_+/, '');
      // Match the theme's existing public -> private photo-property conversion.
      const photo = /^Upload Your Pet(?:'|’)?s Photos?(?:\s*\(\d+\))?\s*:?$/i.test(publicKey) &&
        /^https?:\/\//i.test(text) && /\/front\/files\/|\/files\/|\/uploads\/|cdn\.shopify\.com/i.test(text);
      result[photo ? '_' + publicKey : key] = text;
    });
    return window.LPCheckoutPhotos ? window.LPCheckoutPhotos.normalize(result) : result;
  }

  function expectedItems(response, variantId, quantity) {
    const items = response?.items?.length ? response.items : [response || { variant_id: variantId, quantity }];
    const groups = new Map();
    items.forEach(item => {
      const id = String(item.variant_id || item.id || variantId || '');
      const count = Number(item.quantity ?? item.qty ?? quantity);
      if (!/^\d+$/.test(id) || !Number.isSafeInteger(count) || count < 1) return;
      const properties = propertiesFor(item.properties);
      const key = JSON.stringify([id, Object.entries(properties).sort(([a], [b]) => a.localeCompare(b))]);
      const previous = groups.get(key);
      if (previous) previous.quantity += count;
      else groups.set(key, { id, quantity: count, properties });
    });
    if (!groups.size) throw new Error('Unable to identify your added items. Please review your cart.');
    return [...groups.values()];
  }

  function cartComplete(cart, expected) {
    return expected.every(item => {
      const quantity = (cart.items || []).reduce((total, line) => {
        if (String(line.variant_id) !== item.id) return total;
        const properties = propertiesFor(line.properties);
        if (!Object.entries(item.properties).every(([key, value]) => (properties[key] ?? '') === value)) return total;
        return total + Number(line.quantity || 0);
      }, 0);
      return quantity >= item.quantity;
    });
  }

  function signature(scope) {
    // Exclude generated form IDs/prices; retain the customer's actual APO choices/uploads.
    return JSON.stringify([...scope.querySelectorAll('.mw-option input, .mw-option select, .mw-option textarea, input[name="quantity"], input[data-crossell-variant-id], select[name^="options["], input[name^="options["]')]
      .filter(field => !(field.name === 'quantity' && field.type === 'hidden' && field.hasAttribute('data-mw-apo-hidden-prop')))
      .map(field => [field.name, field.id, field.type, field.value,
        /^(checkbox|radio)$/.test(field.type) ? field.checked : null,
        field.multiple && field.selectedOptions ? [...field.selectedOptions].map(option => option.value) : null,
        field.files ? [...field.files].map(file => [file.name, file.size, file.lastModified]) : null])
      .concat([['lp-contact-email', window.LPProductEmail?.valueFor(scope) || '']]));
  }

  async function readCart() {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(root + 'cart.js', {
      credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error('Unable to verify your cart. Please review it before trying again.');
    const cart = await response.json();
    if (!Array.isArray(cart.items)) throw new Error('Unable to verify your cart. Please review it before trying again.');
    return cart;
  }

  function remember(intent, response, variantId, quantity, partial = false) {
    // APO adds linked gifts separately before the portrait's form submission.
    const children = intent.apoResult?.children || [];
    const items = response?.items?.length ? response.items : [response || { variant_id: variantId, quantity }];
    const receipt = { signature: intent.signature,
      expected: expectedItems({ items: [...items, ...children] }, variantId, quantity), partial };
    intent.receipt = receipt;
    receipts.set(intent.block, receipt);
  }

  function suppressCheckoutDrawer() {
    document.querySelectorAll('cart-drawer, cart-notification').forEach(drawer => {
      if (wrappedDrawers.has(drawer) || typeof drawer.open !== 'function') return;
      const open = drawer.open;
      drawer.open = function () {
        if (pending && pending.method !== 'cart') return;
        // A completed background add must not reopen a drawer the customer closed.
        if (cartDrawerView?.drawer === drawer) {
          if (cartDrawerView.dismissed && !arguments[0]) return;
          if (arguments[0]) cartDrawerView.dismissed = false;
        }
        return open.apply(this, arguments);
      };
      wrappedDrawers.add(drawer);
    });
  }

  function finalButton(button) {
    if (button.closest('quick-add-modal, .mw-tingle')) return false;
    if (button.classList.contains('mw-group__button-next--buy-now')) return false;
    return button.classList.contains('mw-group__button-next--addtocart') ||
      /^add\s+to\s+cart$/i.test(button.textContent.trim());
  }

  function contextFor(block) {
    const main = document.getElementById('MainContent') || document;
    if (block.dataset.giftCheckout === 'true') return giftContextFor(main);
    const suppliedForm = block.dataset.productFormId && document.getElementById(block.dataset.productFormId);
    const preferredScope = suppliedForm && scopeFor(suppliedForm);
    const candidates = [...(preferredScope || main).querySelectorAll(CANDIDATES)].filter(finalButton);
    const add = candidates.find(button => button.getClientRects().length) || candidates[0];
    if (!add) return null;
    const scope = add.closest('product-info') || add.closest('.shopify-section') || main;
    // APO invokes the original (hidden) Add to cart button after preparing its options.
    // Bind to that button's actual form, including themes with duplicate form IDs.
    const original = scope.querySelector('.mw-apo-add-to-cart-hide');
    const form = original?.closest('form') || suppliedForm ||
      [...scope.querySelectorAll('form[data-type="add-to-cart-form"]')].find(form =>
        !form.closest('quick-add-modal'));
    return { form: form || null, scope, add, row: add.closest('.mw-group__button') };
  }

  function giftContextFor(main) {
    // Gift-only adaptation: never bind a recommendation, quick add or app modal.
    const scope = main.querySelector('[id^="MainProduct-"]');
    if (!scope) return null;
    const candidates = [...scope.querySelectorAll(CANDIDATES + ', ' + GIFT_CANDIDATES)]
      .map(node => node.matches('button, input[type="submit"]') ? node : node.querySelector('button, input[type="submit"]'))
      .filter(button => button && !button.closest(GIFT_EXCLUSIONS) && button.getClientRects().length &&
        (finalButton(button) || button.matches('.mw-apo-add-to-cart-custom, .mw-apo-add-to-cart-cloned, .product-form__submit[name="add"]')));
    // App controls own validation. Never bypass them by choosing the theme form first.
    const add = candidates.find(button => !button.matches('.product-form__submit')) || candidates[0];
    if (!add) return null;
    const original = scope.querySelector('.mw-apo-add-to-cart-hide');
    const form = original?.closest('form') || add.closest('form') ||
      [...scope.querySelectorAll('form[data-type="add-to-cart-form"]')].find(form => !form.closest(GIFT_EXCLUSIONS));
    const row = add.closest('.mw-group__button, .product-form__buttons') || add;
    return { form: form || null, scope, add, row, gift: true };
  }

  function status(block, message, error = false) {
    const text = block.querySelector('.lp-direct-checkout__status');
    text.textContent = message;
    text.hidden = !message;
    text.classList.toggle('is-error', error);
  }

  function busy(block, enabled) {
    block.toggleAttribute('aria-busy', enabled);
    block.querySelectorAll('button').forEach(button => { button.disabled = enabled; });
  }

  function validationFailed(intent) {
    const gift = intent.gift;
    release(intent);
    if (gift) status(intent.block,
      'Please complete the required options and upload your pet\'s photo before continuing.', true);
  }

  function release(intent, message = '') {
    if (pending !== intent) return;
    clearTimeout(intent.timeout);
    intent.restoreSubmit?.();
    pending = null;
    busy(intent.block, false);
    status(intent.block, message, !!message);
    if (intent.drawerView) finishCartLoading(intent, message);
  }

  function clearCartLoading(view) {
    if (view.cleared) return;
    view.cleared = true;
    clearTimeout(view.slowTimer);
    view.observer.disconnect();
    view.hidden.forEach(([element, hidden, inert]) => {
      element.hidden = hidden;
      element.inert = inert;
    });
    view.panel.remove();
    view.drawer.classList.remove('lp-apo-cart-pending');
    view.drawer.classList.toggle('is-empty', view.wasEmpty);
    if (view.busy === null) view.inner.removeAttribute('aria-busy');
    else view.inner.setAttribute('aria-busy', view.busy);
  }

  function finishCartLoading(intent, message) {
    const view = intent.drawerView;
    clearTimeout(view.slowTimer);
    if (message) {
      // Keep stale totals and checkout inaccessible when the outcome is uncertain.
      view.inner.removeAttribute('aria-busy');
      view.panel.classList.add('is-error');
      view.panel.querySelector('.lp-apo-cart-progress__title').textContent = 'Please review your cart';
      view.panel.querySelector('.lp-apo-cart-progress__message').textContent = message;
      view.panel.querySelector('button').hidden = false;
      if (view.dismissed) clearCartLoading(view);
      return;
    }
    clearCartLoading(view);
    if (!intent.cartRendered && !view.dismissed) {
      view.dismissed = true;
      // APO validation can fail before the drawer's scheduled opening animation.
      setTimeout(() => { if (cartDrawerView === view) view.drawer.close(); }, 0);
    }
  }

  function startCartLoading(intent, drawer) {
    if (cartDrawerView && !cartDrawerView.cleared) clearCartLoading(cartDrawerView);
    const inner = drawer.querySelector('.drawer__inner');
    const header = inner?.querySelector('.drawer__header');
    if (!inner || !header) return;
    const panel = document.createElement('div');
    panel.className = 'lp-apo-cart-progress';
    panel.setAttribute('role', 'status');
    panel.setAttribute('aria-live', 'polite');
    panel.innerHTML = '<span class="lp-apo-cart-progress__spinner" aria-hidden="true"></span>' +
      '<p class="lp-apo-cart-progress__title">Adding to your cart…</p>' +
      '<p class="lp-apo-cart-progress__message">Saving your selected options and photos.</p>' +
      '<button type="button" class="button" hidden>Back to options</button>';
    const view = { drawer, inner, panel, wasEmpty: drawer.classList.contains('is-empty'),
      busy: inner.getAttribute('aria-busy'), hidden: [], wasOpened: false, dismissed: false };
    for (const element of inner.children) {
      if (element === header) continue;
      view.hidden.push([element, element.hidden, element.inert]);
      element.hidden = true;
      element.inert = true;
    }
    header.after(panel);
    panel.querySelector('button').addEventListener('click', () => drawer.close());
    drawer.classList.add('lp-apo-cart-pending');
    drawer.classList.remove('is-empty');
    inner.setAttribute('aria-busy', 'true');
    view.observer = new MutationObserver(() => {
      if (drawer.classList.contains('active')) view.wasOpened = true;
      else if (view.wasOpened) {
        view.dismissed = true;
        if (pending !== intent) clearCartLoading(view);
      }
    });
    view.observer.observe(drawer, { attributes: true, attributeFilter: ['class'] });
    intent.drawerView = cartDrawerView = view;
    view.slowTimer = setTimeout(() => {
      if (pending === intent) panel.querySelector('.lp-apo-cart-progress__message').textContent =
        'Still adding your item. Please keep this page open.';
    }, 8000);
    drawer.open(intent.add);
  }

  function scopeFor(form) {
    return form.closest('product-info') || form.closest('.main-product') || form.closest('.shopify-section');
  }

  function blocked(button) {
    return button.disabled || button.getAttribute('aria-disabled') === 'true' ||
      button.classList.contains('in-progress') || button.classList.contains('mw-apo-in-progress');
  }

  function bindApoForm(intent, form) {
    if (!form || typeof form.querySelector !== 'function') return;
    const productForm = form.closest('product-form');
    const hasProductHandler = productForm && typeof productForm.onSubmitHandler === 'function';
    if (hasProductHandler) {
      intent.form = form; // This path finishes through the existing product-form.js handler.
      if (intent.method !== 'cart') return;
    }
    if (intent.restoreSubmit) return;
    // APO may select the installment form and invoke form.submit() directly.
    // Native submit navigates to /cart; its add has not finished at clickFinally.
    const original = form.submit;
    if (typeof original !== 'function') return;
    const descriptor = Object.getOwnPropertyDescriptor(form, 'submit');
    const submit = () => {
      if (intent.submission) return intent.submission;
      intent.phase = 'adding';
      intent.submission = (async () => {
        const data = new FormData(form);
        if (window.LPProductEmail) {
          // Use the email captured with this intent, including app-rebuilt forms.
          if (intent.email) data.set('properties[_Contact email]', intent.email);
          else data.delete('properties[_Contact email]');
        }
        const root = window.Shopify?.routes?.root || '/';
        if (intent.method === 'cart') {
          const drawer = document.querySelector('cart-drawer');
          const ids = drawer?.getSectionsToRender().map(section => section.id) || [];
          if (ids.length) {
            data.set('sections', ids.join(','));
            data.set('sections_url', window.location.pathname);
          }
        }
        const response = await fetch(root + 'cart/add.js', {
          method: 'POST', credentials: 'same-origin',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: data
        });
        const added = await response.json();
        if (!response.ok || added.status === 'error' || Number(added.status) >= 400) {
          throw new Error(added.description || added.message || 'Unable to add your options. Please try again.');
        }
        if (pending !== intent) return;
        await window.LPDirectCheckout.complete(intent, added, data.get('id'), Number(data.get('quantity') || 1));
      })().catch(error => release(intent, error.message));
      return intent.submission;
    };
    form.submit = function () {
      if (pending !== intent) return original.apply(this, arguments);
      return submit();
    };
    const onSubmit = event => {
      if (pending !== intent || !intent.apoResult) return;
      // Normal submit events still belong to product-form.js. Only a direct
      // form.submit() call needs the native bridge when that component exists.
      if (hasProductHandler) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      submit();
    };
    form.addEventListener('submit', onSubmit, true);
    intent.restoreSubmit = () => {
      form.removeEventListener('submit', onSubmit, true);
      if (descriptor) Object.defineProperty(form, 'submit', descriptor);
      else delete form.submit;
      intent.restoreSubmit = null;
    };
    intent.form = hasProductHandler ? form : null;
  }

  async function start(block, method) {
    if (pending) return;
    const context = contextFor(block);
    if (!context || !context.add.getClientRects().length || blocked(context.add) || draft.restoring) return;
    syncDraftReceipt(block, context);
    const { form, scope, add } = context;
    if (window.LPProductEmail?.prepare(scope) === false) return;
    if (!form && !window.__LP_DIRECT_APO_EVENTS__) {
      status(block, 'Please refresh this page to load checkout.', true);
      return;
    }
    const intent = { ...context, block, method, email: window.LPProductEmail?.valueFor(scope) || '', signature: signature(scope), phase: 'validating', redirecting: false };
    intent.draftKey = !context.gift && portraitContext(scope) ? draftConfigurationKey(draftConfiguration()) : '';
    pending = intent;
    busy(block, true);
    status(block, 'Preparing your checkout…');
    intent.timeout = setTimeout(() => release(intent,
      'Checkout could not finish. Please check your cart before trying again.'), 120000);
    const receipt = receipts.get(block);
    if (receipt && receipt.signature === intent.signature) {
      intent.phase = 'verifying';
      intent.receipt = receipt;
      try {
        const cart = await readCart();
        if (pending !== intent) return;
        const anyAdded = receipt.expected.some(item => cart.items.some(line => String(line.variant_id) === item.id));
        if (anyAdded) {
          await window.LPDirectCheckout.complete(intent, null, null, null, cart);
          return;
        }
        // All previously added variants were removed. It is safe to run a fresh add.
        receipts.delete(block);
        intent.receipt = null;
        intent.phase = 'validating';
      } catch (error) { release(intent, error.message); return; }
    }
    // Reuse APO validation/add; retrying a known successful add never creates another request.
    add.click();
    checkValidation(intent);
  }

  function returnedReceipt(block, context) {
    const returned = returnedCarts.get(block);
    return returned && context && returned.receipt.signature === signature(context.scope) ? returned : null;
  }

  function returnedItemPresent(cart, receipt) {
    const item = receipt.expected[0];
    if (!item) return false;
    return cart.items.some(line => {
      if (String(line.variant_id) !== item.id || Number(line.quantity) < 1) return false;
      const properties = propertiesFor(line.properties);
      return Object.entries(item.properties).every(([key, value]) => {
        // APO changes these counts/display attributes when cart quantity changes.
        if (['_mws_qty', '_mws_products', '__mws_apo_cta'].includes(key)) return true;
        if (key === 'Matching Pet Gifts' && item.properties._mws_apo_cfg) return true;
        return (properties[key] ?? '') === value;
      });
    });
  }

  function syncReturnedButton(block, context) {
    if (pending || context.gift) return;
    const label = [...context.add.childNodes].find(node => node.nodeType === 3 &&
      /^(?:add\s+to\s+cart|view\s+cart)$/i.test(node.textContent.trim()));
    if (!label || (!returnedCarts.has(block) && label.textContent.trim() !== 'View cart')) return;
    const returned = returnedReceipt(block, context);
    const text = returned && returned.present !== false ? 'View cart' : 'Add to cart';
    if (label.textContent.trim() !== text) label.textContent = text;
  }

  async function refreshReturnedCarts() {
    const entries = [...document.querySelectorAll('.lp-direct-checkout')]
      .map(block => [block, returnedCarts.get(block)]).filter(([, returned]) => returned);
    if (!entries.length || pending) return;
    const request = ++returnedCartRequest;
    try {
      const cart = await readCart();
      if (request !== returnedCartRequest) return;
      updateDraftCart(cart);
      entries.forEach(([block, returned]) => {
        if (returnedCarts.get(block) === returned) returned.present = returnedItemPresent(cart, returned.receipt);
      });
      schedule();
    } catch (_) { /* A click rechecks the cart before deciding whether to add. */ }
  }

  async function openReturnedCart(block, context, viewOnly = false) {
    const returned = returnedReceipt(block, context);
    if (pending || !returned || blocked(context.add)) return;
    const intent = { ...context, block, method: 'cart', phase: 'verifying',
      signature: returned.receipt.signature, receipt: returned.receipt };
    pending = intent;
    ++returnedCartRequest;
    busy(block, true);
    status(block, 'Opening your cart…');
    intent.timeout = setTimeout(() => release(intent,
      'Please check your cart before trying again.'), 120000);
    try {
      const cart = await readCart();
      if (pending !== intent) return;
      if (signature(context.scope) !== intent.signature) {
        release(intent);
        schedule();
        return;
      }
      returned.present = returnedItemPresent(cart, returned.receipt);
      if (returned.present) {
        await showAddedCart(intent, cart);
      } else {
        returnedCarts.delete(block);
        if (receipts.get(block) === returned.receipt) receipts.delete(block);
        if (viewOnly) {
          await showAddedCart(intent, cart);
          syncReturnedButton(block, context);
          return;
        }
        release(intent);
        syncReturnedButton(block, context);
        startCart(block, context);
        if (pending?.block === block && pending.method === 'cart') context.add.click();
      }
    } catch (error) { release(intent, error.message); }
  }

  function startCart(block, context) {
    if (window.LPProductEmail?.prepare(context.scope) === false) return;
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || typeof drawer.renderContents !== 'function' ||
        !window.__LP_DIRECT_APO_EVENTS__ || blocked(context.add)) return;
    suppressCheckoutDrawer();
    // Arm the existing APO bridge without clicking again or bypassing its validation.
    const intent = { ...context, block, method: 'cart', email: window.LPProductEmail?.valueFor(context.scope) || '', signature: signature(context.scope),
      phase: 'validating', redirecting: false };
    intent.draftKey = !context.gift && portraitContext(context.scope) ? draftConfigurationKey(draftConfiguration()) : '';
    pending = intent;
    busy(block, true);
    status(block, '');
    // Portrait groups validate before APO starts submitting. Keep invalid clicks on the PDP.
    if (context.gift || !portraitContext(context.scope)) startCartLoading(intent, drawer);
    intent.timeout = setTimeout(() => release(intent,
      'Please check your cart before trying again.'), 120000);
    checkValidation(intent);
  }

  async function showAddedCart(intent, cart) {
    const drawer = document.querySelector('cart-drawer');
    const ids = drawer.getSectionsToRender().filter(section => section.selector
      ? document.querySelector(section.selector) : document.getElementById(section.id))
      .map(section => section.id);
    const valid = sections => ids.length && ids.every(id => typeof sections?.[id] === 'string' && sections[id]);
    let sections = cart.sections;
    if (!valid(sections)) {
      const root = window.Shopify?.routes?.root || '/';
      const url = new URL(root + 'cart', window.location.origin);
      url.searchParams.set('sections', ids.join(','));
      const response = await fetch(url.href, { credentials: 'same-origin', cache: 'no-store' });
      if (!response.ok) throw new Error('Your item was added. Please open your cart to review it.');
      sections = await response.json();
      if (!valid(sections)) {
        throw new Error('Your item was added. Please open your cart to review it.');
      }
    }
    if (pending !== intent) return;
    drawer.setActiveElement(intent.add);
    // Release the checkout-only open guard before the drawer renders and opens.
    intent.cartRendered = true;
    release(intent);
    drawer.renderContents({ ...cart, sections });
  }

  function checkValidation(intent) {
    setTimeout(() => {
      if (pending !== intent || intent.phase !== 'validating') return;
      const scope = intent.scope;
      const running = scope && scope.querySelector('.mw-apo-in-progress, .mw-group__button-next.in-progress');
      if (running) { checkValidation(intent); return; }
      release(intent); // APO keeps its own required-field or upload error visible.
    }, 400);
  }

  window.LPDirectCheckout = {
    take(form) {
      if (!pending || !pending.form || pending.form !== form || pending.phase !== 'validating') return null;
      pending.phase = 'adding';
      return pending;
    },
    fail(intent, message) { release(intent, message || 'Please check your options and try again.'); },
    remember,
    async complete(intent, response, variantId, quantity, existingCart) {
      if (pending !== intent) throw new Error('Please check your cart before trying again.');
      if (window.LPProductEmail && response) {
        response = await window.LPProductEmail.complete(intent.scope, response, intent.email, intent.method);
        if (pending !== intent) throw new Error('Please check your cart before trying again.');
      }
      if (!intent.receipt) remember(intent, response, variantId, quantity);
      // A successful add response already confirms this submission. Shopify can
      // return the updated drawer HTML in that same request; checkout alone needs
      // a separate cart read to verify all expected lines before redirecting.
      if (intent.method === 'cart') {
        if (intent.receipt.partial) throw new Error('Some items could not be added. Please review your cart.');
        markDraftAdded(intent);
        await showAddedCart(intent, response || {});
        return;
      }
      const cart = existingCart || await readCart();
      const complete = !intent.receipt.partial && cartComplete(cart, intent.receipt.expected);
      if (!complete) throw new Error('Your cart is missing some selected options. Please review it before checking out.');
      const root = window.Shopify?.routes?.root || '/';
      if (window.LPCheckoutPhotos) await window.LPCheckoutPhotos.prepareCheckout(cart);
      if (pending !== intent) throw new Error('Please check your cart before trying again.');
      clearTimeout(intent.timeout);
      intent.restoreSubmit?.();
      intent.redirecting = true;
      markDraftAdded(intent);
      saveProductDraft();
      status(intent.block, 'Opening checkout…');
      // Use the current Shopify cart session, preserving every line and its properties.
      // Do not reconstruct a cart permalink (properties only apply to its first item).
      const checkout = new URL(root + 'checkout', window.location.origin);
      if (intent.method === 'shop_pay') checkout.searchParams.set('payment', 'shop_pay');
      const destination = window.LPProductEmail?.checkoutURL(checkout, intent.email) || checkout;
      window.location.assign(destination.href);
    }
  };

  function mount() {
    frame = null;
    suppressCheckoutDrawer();
    document.querySelectorAll('.lp-direct-checkout').forEach(block => {
      const context = contextFor(block);
      const row = context?.row;
      if (!row) { if (!block.hidden) block.hidden = true; return; }
      const { add } = context;
      const duplicate = row.nextElementSibling;
      if (duplicate && duplicate !== block && duplicate.classList.contains('lp-direct-checkout')) {
        if (!block.hidden) block.hidden = true;
        return;
      }
      if (row.nextElementSibling !== block) row.after(block);
      if (block.hidden) block.hidden = false;
      if (!pending) busy(block, blocked(add));
      syncDraftReceipt(block, context);
      syncReturnedButton(block, context);
      // Gift nodes can be rebuilt by the app with copied data attributes.
      // Their checkout clicks are delegated independently of per-node listeners.
      if (block.dataset.giftCheckout === 'true') return;
      if (block.dataset.bound) return;
      block.dataset.bound = 'true';
      block.querySelectorAll('[data-checkout-method]').forEach(button => {
        button.addEventListener('click', event => {
          event.preventDefault();
          event.stopPropagation();
          start(block, button.dataset.checkoutMethod);
        });
      });
    });
  }

  function schedule() { if (frame === null) frame = requestAnimationFrame(mount); }
  function init() {
    mount();
    // Release a completed checkout intent when Back restores this document.
    window.addEventListener('pageshow', event => {
      if (!event.persisted || !pending?.redirecting) return;
      const intent = pending;
      release(intent);
      if (!intent.gift && document.body.classList.contains('lp-portrait-pdp')) {
        returnedCarts.set(intent.block, { receipt: intent.receipt, present: true });
        refreshReturnedCarts();
      }
      schedule();
    });
    const optionsChanged = event => {
      if (!event.target.closest?.('[id^="MainProduct-"]')) return;
      queueProductDraft();
      if ([...document.querySelectorAll('.lp-direct-checkout')].some(block => returnedCarts.has(block))) schedule();
    };
    document.addEventListener('input', optionsChanged);
    document.addEventListener('change', optionsChanged);
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, refreshReturnedCarts);
    }
    // Handle gift checkout before app/form click delegation at document level.
    // This survives cloned/replaced nodes and still clicks the original app add.
    window.addEventListener('click', event => {
      const button = event.target.closest?.('.lp-direct-checkout[data-gift-checkout="true"] [data-checkout-method]');
      if (!button) return;
      const block = button.closest('.lp-direct-checkout');
      if (!block || block.hidden || button.disabled) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      start(block, button.dataset.checkoutMethod);
    }, true);
    document.addEventListener('lp:apo-event', event => {
      const intent = pending;
      if (!intent) return;
      const { name, data } = event.detail || {};
      if (name === 'attachEvents::initAddToCart::clickStart') {
        bindApoForm(intent, data?.htmlElements?.form);
        if (!intent.form) intent.phase = 'apo-adding';
        if (intent.method === 'cart' && !intent.drawerView && !intent.gift && portraitContext(intent.scope)) {
          const drawer = document.querySelector('cart-drawer');
          if (drawer) startCartLoading(intent, drawer);
        }
      }
      if (name === 'onClickFinish') intent.apoResult = JSON.parse(JSON.stringify(data));
      if (intent.method === 'cart' && name === 'onClick::validate-failed') {
        validationFailed(intent);
        return;
      }
      if (intent.method === 'cart' && name === 'attachEvents::initAddToCart::clickError') {
        release(intent, 'Unable to add your options. Please check your cart before trying again.');
        return;
      }
      if (intent.form) return; // Native product forms finish through product-form.js instead.
      if (name === 'onClick::validate-failed') validationFailed(intent);
      if (name === 'attachEvents::initAddToCart::clickError') {
        release(intent, 'Unable to add your options. Please check your cart before trying again.');
      }
      if (name === 'attachEvents::initAddToCart::clickFinally') {
        if (intent.submission || intent.phase === 'verifying' || intent.redirecting) return;
        if (!intent.apoResult) { release(intent); return; }
        intent.phase = 'verifying';
        const result = intent.apoResult;
        window.LPDirectCheckout.complete(intent, result, result.variant_id, result.qty || 1)
          .catch(error => release(intent, error.message));
      }
    });
    new MutationObserver(schedule).observe(document.getElementById('MainContent') || document.body,
      { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
    window.addEventListener('pagehide', saveProductDraft);
    document.addEventListener('visibilitychange', () => { if (document.hidden) saveProductDraft(); });
    // Avoid a second trusted add click while the first checkout is preparing.
    document.addEventListener('click', event => {
      if (!event.isTrusted && !window.LPProductEmail?.isResumedAction(event.target)) return;
      if (event.target.closest?.('[id^="MainProduct-"] .mw-group__button')) {
        if (draft.restoring) { event.preventDefault(); event.stopImmediatePropagation(); return; }
        setTimeout(queueProductDraft, 0);
      }
      if (!pending) {
        const giftBlock = [...document.querySelectorAll('.lp-direct-checkout[data-gift-checkout="true"]')].find(block => !block.hidden);
        const giftContext = giftBlock && contextFor(giftBlock);
        const giftAdd = giftContext?.add;
        if (giftAdd && (giftAdd === event.target || giftAdd.contains(event.target))) {
          startCart(giftBlock, giftContext);
          return;
        }
        const add = event.target.closest(CANDIDATES);
        if (!add || !finalButton(add) || !add.closest('[id^="MainProduct-"]')) return;
        const block = [...document.querySelectorAll('.lp-direct-checkout')].find(block =>
          !block.hidden && contextFor(block)?.add === add);
        if (block) {
          const context = contextFor(block);
          syncDraftReceipt(block, context);
          if (returnedReceipt(block, context)) {
            event.preventDefault();
            event.stopImmediatePropagation();
            openReturnedCart(block, context, /^view\s+cart$/i.test(context.add.textContent.trim()));
            return;
          }
          startCart(block, context);
        }
        return;
      }
      const target = event.target.closest(pending.gift
        ? '.mw-group__button, .mw-apo-add-to-cart-custom, .mw-apo-add-to-cart-cloned, .product-form__submit'
        : '.mw-group__button, .mw-apo-add-to-cart-cloned, .product-form__submit');
      if (target && pending.scope?.contains(target)) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }, true);
  }
  document.addEventListener('lp:apo-event', event => {
    const { name, data } = event.detail || {};
    handleDraftApoEvent(name, data);
  });
  const earlyDraft = window.__LP_PORTRAIT_DRAFT_INIT__;
  if (earlyDraft && !draft.data) {
    handleDraftApoEvent('init::insertIntoInline::afterHtmlInserted', earlyDraft.data);
    if (earlyDraft.finished) handleDraftApoEvent('init::finish', earlyDraft.data);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
